import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { parseMarkdown } from '../../utils/sharedResponseUtils';

/**
 * CollapsibleList Component
 * Detects and renders numbered and bulleted lists with collapsible/expandable items
 * Shows a compact summary first, then allows expansion for details
 */
const CollapsibleList = ({ content, minItems = 2 }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);

  // Helper: determine if a block of text looks like a list (to avoid rendering duplicates)
  const isLikelyListText = (text) => {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    if (!trimmed) return false;
    const lines = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
    if (lines.length === 0) return false;
    const listLines = lines.filter(line => /^(\d+\.\s+|[•\-\*]\s+)/.test(line));
    // Consider it a list if majority of lines look like list entries
    return listLines.length > 0 && listLines.length >= lines.length * 0.6;
  };

  // Detect both numbered and bulleted list patterns
  // Note: content should already be processed through formatMessageContent
  // which converts plain URLs/emails to markdown links
  const detectListItems = (text) => {
    const lines = text.split('\n');
    const items = [];
    let currentItem = null;
    let inListSection = false;

    lines.forEach((line, index) => {
      // Match numbered list items: "1. ", "2. ", etc. (may have leading spaces)
      // Capture everything after the number to preserve markdown formatting
      const numberedMatch = line.match(/^\s*(\d+)\.\s+(.+)$/);
      // Match bulleted list items: "• ", "- ", "* " (may have leading spaces)
      // Capture everything after the bullet to preserve markdown formatting
      const bulletMatch = line.match(/^\s*[•\-\*]\s+(.+)$/);
      
      if (numberedMatch) {
        // Save previous item if exists
        if (currentItem) {
          items.push(currentItem);
        }
        
        inListSection = true;
        // Start new item - preserve full title text (may contain markdown like **bold** or [links](url))
        const titleText = numberedMatch[2];
        currentItem = {
          type: 'numbered',
          number: numberedMatch[1],
          title: titleText.trim(), // Trim whitespace but preserve markdown formatting
          details: [],
          startIndex: index,
          fullText: line
        };
      } else if (bulletMatch) {
        // Save previous item if exists
        if (currentItem) {
          items.push(currentItem);
        }
        
        inListSection = true;
        // Start new bulleted item - preserve full title text (may contain markdown like **bold** or [links](url))
        const titleText = bulletMatch[1];
        currentItem = {
          type: 'bulleted',
          title: titleText.trim(), // Trim whitespace but preserve markdown formatting
          details: [],
          startIndex: index,
          fullText: line
        };
      } else if (currentItem) {
        const trimmed = line.trim();
        
        // CRITICAL: Check if this is a new list item FIRST before checking if it's a detail line
        const isNewNumberedItem = trimmed.match(/^\d+\.\s+/);
        const isNewBulletItem = trimmed.match(/^[•\-\*]\s+/);
        
        // Check if this is a detail line (Thai and English field names)
        // Only consider as detail if it's NOT a new list item
        const isDetailLine = !isNewNumberedItem && !isNewBulletItem && trimmed && (
          trimmed.startsWith('Email:') ||
          trimmed.startsWith('Phone:') ||
          trimmed.startsWith('Company:') ||
          trimmed.startsWith('Industry:') ||
          trimmed.startsWith('Address:') ||
          trimmed.startsWith('Customer ID') ||
          trimmed.startsWith('Found in:') ||
          trimmed.startsWith('Created:') ||
          trimmed.startsWith('ชื่อ:') || // Thai: Name
          trimmed.startsWith('ตำแหน่ง:') || // Thai: Position
          trimmed.startsWith('อีเมล:') || // Thai: Email
          trimmed.startsWith('โทรศัพท์:') || // Thai: Phone
          trimmed.startsWith('อุตสาหกรรม:') || // Thai: Industry
          trimmed.startsWith('ที่อยู่:') || // Thai: Address
          trimmed.startsWith('คุณ') // Thai name prefix
        );
        
        if (isNewNumberedItem) {
          // New numbered item - save current and start new
          items.push(currentItem);
          const newMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
          if (newMatch) {
            currentItem = {
              type: 'numbered',
              number: newMatch[1],
              title: newMatch[2].trim(),
              details: [],
              startIndex: index,
              fullText: line
            };
          } else {
            currentItem = null;
          }
        } else if (isNewBulletItem) {
          // New bulleted item - save current and start new
          items.push(currentItem);
          const newMatch = trimmed.match(/^[•\-\*]\s+(.+)$/);
          if (newMatch) {
            currentItem = {
              type: 'bulleted',
              title: newMatch[1].trim(),
              details: [],
              startIndex: index,
              fullText: line
            };
          } else {
            currentItem = null;
          }
        } else if (isDetailLine || (trimmed === '' && currentItem.details.length > 0)) {
          // Detail line or empty line after details
          currentItem.details.push(line); // Keep original line (with indentation if any)
          currentItem.fullText += '\n' + line;
        } else if (trimmed && inListSection) {
          // Encountered non-detail text -> treat as paragraph outside the list
          items.push(currentItem);
          currentItem = null;
          inListSection = false;
        } else if (!trimmed && currentItem && currentItem.details.length > 0) {
          // Empty line - might be separator between items
          const nextLine = index + 1 < lines.length ? lines[index + 1].trim() : '';
          if (nextLine.match(/^[\d•\-\*]\.?\s+/)) {
            // Next is a new item, end current item
            items.push(currentItem);
            currentItem = null;
          } else {
            // Keep empty line as detail separator (but skip if it's just spacing)
            // Only keep if there are already details
            if (currentItem.details.length > 0 && currentItem.details[currentItem.details.length - 1] !== '') {
              // Skip empty lines to reduce spacing
            }
          }
        }
      }
    });

    // Don't forget the last item
    if (currentItem) {
      items.push(currentItem);
    }

    return items;
  };

  const listItems = detectListItems(content);

  // If no list items detected or less than minItems, check for simple bulleted lists
  if (listItems.length < minItems) {
    // Check for simple bulleted lists (like customer info fields)
    const simpleBulletPattern = /^[•\-\*]\s+.*(?:ชื่อ|ตำแหน่ง|อีเมล|โทรศัพท์|อุตสาหกรรม|ที่อยู่|Email|Phone|Company|Industry|Address|Position)/m;
    if (simpleBulletPattern.test(content)) {
      // Render as compact simple list with proper markdown processing
      // Content is already formatted (with formatMessageContent) so links should be in markdown format
      const processedContent = parseMarkdown(content);
      return (
        <div
          className="compact-list-container"
          style={{
            lineHeight: '1.6',
            paddingLeft: '0.25rem'
          }}
        >
          <div 
            className="text-sm space-y-1"
            dangerouslySetInnerHTML={{ 
              __html: processedContent.replace(/<li/g, '<li style="margin: 4px 0; padding: 2px 0; line-height: 1.6;"')
            }}
          />
        </div>
      );
    }
    // No list detected - render content normally with markdown processing
    // This ensures hyperlinks and bold text are properly rendered
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
      />
    );
  }

  const toggleItem = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const toggleAll = () => {
    if (showAllDetails) {
      setExpandedItems(new Set());
      setAllExpanded(false);
      setShowAllDetails(false);
    } else {
      const allIndexes = new Set(listItems.map((_, i) => i));
      setExpandedItems(allIndexes);
      setAllExpanded(true);
      setShowAllDetails(true);
    }
  };

  // Split content into parts: before list, list items, after list
  const parts = [];
  
  if (listItems.length === 0) {
    // No list detected - render content normally with markdown processing
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
      />
    );
  }

  // Find the first list item in content
  const firstItemLine = listItems[0].fullText.split('\n')[0].trim();
  const firstItemIndex = content.indexOf(firstItemLine);
  
  // Add content before first list item
  if (firstItemIndex > 0) {
    const beforeContent = content.substring(0, firstItemIndex).trim();
    if (beforeContent && !isLikelyListText(beforeContent)) {
      parts.push({
        type: 'text',
        content: beforeContent
      });
    }
  }

  // Add all list items
  listItems.forEach((item, itemIndex) => {
    parts.push({
      type: 'listItem',
      item: item,
      index: itemIndex
    });
  });

  // Find content after last list item
  const lastItem = listItems[listItems.length - 1];
  const lastItemEnd = content.indexOf(lastItem.fullText) + lastItem.fullText.length;
  if (lastItemEnd < content.length) {
    const afterContent = content.substring(lastItemEnd).trim();
    if (afterContent && !isLikelyListText(afterContent)) {
      parts.push({
        type: 'text',
        content: afterContent
      });
    }
  }

  return (
    <div className="collapsible-list-container" style={{ lineHeight: '1.5' }}>
      {parts.map((part, partIndex) => {
        if (part.type === 'text') {
          // Render text parts normally (will be processed by parseMarkdown)
          return (
            <div 
              key={partIndex} 
              className="mb-2 text-sm"
              style={{ lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: parseMarkdown(part.content) }}
            />
          );
        }

        // Render list items with collapsible functionality
        const { item, index } = part;
        const isExpanded = expandedItems.has(index);
        const cleanedDetails = (item.details || []).filter(d => d && d.trim());
        const hasDetails = cleanedDetails.length > 0;
        const detailsToShow = showAllDetails ? cleanedDetails : cleanedDetails.slice(0, 5);
        const hasHiddenDetails = !showAllDetails && cleanedDetails.length > detailsToShow.length;

        return (
          <div key={`list-item-${partIndex}-${index}`} className="mb-2 pl-2">
            <div 
              className={`flex items-start gap-2 ${hasDetails ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''} rounded py-1 px-2 -ml-2 transition-colors`}
              onClick={() => hasDetails && toggleItem(index)}
            >
              {hasDetails && (
                <div className="mt-0.5 flex-shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div 
                  className={`text-sm ${hasDetails ? 'font-semibold' : 'font-medium'} text-gray-900 dark:text-white`}
                  style={{ lineHeight: '1.6', wordBreak: 'break-word' }}
                >
                  {item.type === 'numbered' && (
                    <span className={`${hasDetails ? 'font-bold' : 'font-medium'} mr-1`}>{item.number}.</span>
                  )}
                  {item.type === 'bulleted' && (
                    <span className={`${hasDetails ? 'font-bold' : 'font-medium'} mr-1`}>•</span>
                  )}
                  <span dangerouslySetInnerHTML={{ __html: parseMarkdown(item.title.trim()) }} />
                </div>
                {isExpanded && hasDetails && (
                  <div 
                    className="mt-2 pl-6 text-xs text-gray-700 dark:text-gray-300 leading-relaxed border-l-2 border-gray-200 dark:border-gray-600"
                    style={{ lineHeight: '1.6' }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ 
                        __html: parseMarkdown(detailsToShow.join('\n'))
                      }}
                    />
                    {hasHiddenDetails && (
                      <div className="mt-1 text-[11px] italic text-gray-500 dark:text-gray-400">
                        Showing 5 of {cleanedDetails.length} items. Use "Expand All" to view more.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      
      {listItems.length >= minItems && listItems.some(item => {
        const cleanedDetails = (item.details || []).filter(d => d && d.trim());
        return cleanedDetails.length > 0;
      }) && (
        <div className="mt-1.5">
          <button
            onClick={toggleAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
          >
            {showAllDetails ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CollapsibleList;
